import { useState, useEffect } from 'react';
import { iotApi } from '../api/client';
import type { IoTDevice } from '../api/client';
import { useApi } from '../hooks/useApi';
import { T, SectionHeader, Card, Chip, Skeleton } from '../components/ui';

const DEVICE_ICON: Record<string, string> = {
 temperature: '🌡', humidity: '💧', shock: '⚡', gps: '📍',
};
const DEVICE_COLOR: Record<string, string> = {
 temperature: T.sapphire, humidity: T.teal, shock: T.amber, gps: T.sage,
};
const THRESHOLDS: Record<string, { min: number; max: number; unit: string }> = {
 temperature: { min: -2, max: 8, unit: '°C' },
 humidity: { min: 80, max: 95, unit: '%RH' },
 shock: { min: 0, max: 2.5, unit: 'g' },
};

function GaugeBar({ value, type }: { value: number; type: string }) {
 const t = THRESHOLDS[type];
 if (!t) return null;
 const pct = Math.min(100, Math.max(0, ((value - t.min) / (t.max - t.min)) * 100));
 const violated = value < t.min || value > t.max;
 return (
 <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
 <span style={{ fontSize: 9, color: T.dim, width: 28, textAlign: 'right' }}>{t.min}</span>
 <div style={{ flex: 1, background: T.border, borderRadius: 4, height: 6, overflow: 'hidden' }}>
 <div style={{ height: '100%', width: `${pct}%`, background: violated ? T.crimson : T.sage, transition: 'width .4s, background .3s' }} />
 </div>
 <span style={{ fontSize: 9, color: T.dim, width: 28 }}>{t.max}{t.unit}</span>
 </div>
 );
}

export default function IotPage() {
 const [alertOnly, setAlertOnly] = useState(false);
 const [selectedDevice, setDevice] = useState<IoTDevice | null>(null);

 const { data: readingsData, loading: readingsLoading, refetch: refetchReadings } = useApi(
 () => iotApi.readings({ alert_only: alertOnly || undefined, limit: 200 }), [alertOnly]
 );
 const { data: devicesData, loading: devicesLoading } = useApi(() => iotApi.devices(), []);

 const readings = readingsData?.readings || [];
 const devices = devicesData?.devices || [];

 // Auto-refresh every 30 seconds
 useEffect(() => {
 const id = setInterval(refetchReadings, 30_000);
 return () => clearInterval(id);
 }, [refetchReadings]);

 const filteredReadings = selectedDevice
 ? readings.filter(r => r.device_id === selectedDevice.device_id)
 : readings;

 const violations = readings.filter(r => r.alert_triggered).length;
 const activeDevices = devices.length;

 return (
 <div className="fade-up">
 <SectionHeader
 title="IoT Cold Chain Monitoring"
 sub="Real-time sensor readings from refrigerated transport and storage. Violations drop provenance scores and fire alerts."
 right={
 <div style={{ display: 'flex', gap: 8 }}>
 <Chip label={`${activeDevices} Devices`} color={T.sapphire} />
 <Chip label={`${violations} Violations`} color={violations > 0 ? T.crimson : T.sage} dot={violations > 0} />
 </div>
 }
 />

 <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20 }}>
 {/* Device list */}
 <div>
 <div style={{ fontSize: 10, color: T.sub, textTransform: 'uppercase', letterSpacing: .8, marginBottom: 10 }}>Devices ({activeDevices})</div>
 <div
 onClick={() => setDevice(null)}
 style={{
 background: !selectedDevice ? T.lift : T.panel,
 border: `1px solid ${!selectedDevice ? T.copper + '40' : T.border}`,
 borderRadius: 9, padding: '10px 12px', marginBottom: 8, cursor: 'pointer',
 }}
 >
 <div style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>All Devices</div>
 <div style={{ fontSize: 10, color: T.sub }}>{readings.length} readings</div>
 </div>

 {devicesLoading
 ? [1,2,3].map(i => <div key={i} style={{ marginBottom: 6 }}><Skeleton h={56} /></div>)
 : devices.map(d => (
 <div key={d.device_id} onClick={() => setDevice(selectedDevice?.device_id === d.device_id ? null : d)} style={{
 background: selectedDevice?.device_id === d.device_id ? T.lift : T.panel,
 border: `1px solid ${selectedDevice?.device_id === d.device_id ? DEVICE_COLOR[d.device_type] + '50' : d.violations > 0 ? T.crimson + '30' : T.border}`,
 borderLeft: `3px solid ${d.violations > 0 ? T.crimson : DEVICE_COLOR[d.device_type] || T.sub}`,
 borderRadius: 9, padding: '10px 12px', marginBottom: 6, cursor: 'pointer', transition: 'all .12s',
 }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
 <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
 <span>{DEVICE_ICON[d.device_type] || '📡'}</span>
 <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: T.text, fontWeight: 500 }}>{d.device_id}</span>
 </div>
 {d.violations > 0 && <Chip label={`${d.violations} violations`} color={T.crimson} />}
 </div>
 <div style={{ fontSize: 10, color: T.sub }}>
 {d.total_readings.toLocaleString()} readings · {new Date(d.last_seen).toLocaleTimeString()}
 </div>
 </div>
 ))}
 </div>

 {/* Readings feed */}
 <div>
 {/* Controls */}
 <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
 <button onClick={() => setAlertOnly(a => !a)} style={{
 background: alertOnly ? `${T.crimson}20` : T.panel,
 border: `1px solid ${alertOnly ? T.crimson + '40' : T.border}`,
 color: alertOnly ? T.crimson : T.sub,
 borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 11, fontWeight: alertOnly ? 700 : 400,
 }}>
 {alertOnly ? '⚠ Violations only' : 'All readings'}
 </button>
 <button onClick={refetchReadings} style={{
 background: T.panel, border: `1px solid ${T.border}`,
 color: T.sub, borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 11,
 }}>↻ Refresh</button>
 <span style={{ fontSize: 10, color: T.dim, marginLeft: 'auto' }}>Auto-refresh every 30s</span>
 </div>

 {/* Stats row */}
 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
 {[
 ['Total Readings', readings.length, T.sub],
 ['Violations', violations, violations > 0 ? T.crimson : T.sage],
 ['Active Devices', activeDevices, T.sapphire],
 ['Products Affected', new Set(readings.filter(r => r.alert_triggered).map(r => r.product_id)).size, violations > 0 ? T.amber : T.sage],
 ].map(([l, v, c]) => (
 <Card key={String(l)} style={{ padding: '12px', textAlign: 'center' }}>
 <div style={{ fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 20, color: String(c) }}>{v}</div>
 <div style={{ fontSize: 9, color: T.sub, textTransform: 'uppercase', letterSpacing: .6, marginTop: 3 }}>{l}</div>
 </Card>
 ))}
 </div>

 {/* Readings grid */}
 {readingsLoading
 ? <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
 {[1,2,3,4].map(i => <Skeleton key={i} h={100} />)}
 </div>
 : filteredReadings.length === 0
 ? <Card style={{ textAlign: 'center', padding: '40px' }}>
 <div style={{ color: T.sub, fontSize: 12 }}>{alertOnly ? 'No violations' : 'No readings'} for the selected filter</div>
 </Card>
 : (
 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 10 }}>
 {filteredReadings.map(r => {
 const t = THRESHOLDS[r.device_type];
 return (
 <div key={r.id} style={{
 background: r.alert_triggered ? `${T.crimson}08` : T.panel,
 border: `1px solid ${r.alert_triggered ? T.crimson + '35' : T.border}`,
 borderRadius: 10, padding: '13px 15px',
 }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
 <div>
 <div style={{ fontSize: 11, fontWeight: 600, color: T.text }}>{r.product_name || 'Unknown product'}</div>
 <div style={{ fontSize: 10, color: T.sub, marginTop: 2 }}>{r.location_label || r.device_id}</div>
 </div>
 <div style={{ textAlign: 'right' }}>
 <div style={{
 fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 22,
 color: r.alert_triggered ? T.crimson : T.sage,
 }}>
 {r.value}<span style={{ fontSize: 11 }}>{r.unit}</span>
 </div>
 {r.alert_triggered && <Chip label="VIOLATION" color={T.crimson} />}
 </div>
 </div>

 {t && <GaugeBar value={r.value} type={r.device_type} />}

 <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 9, color: T.dim }}>
 <span style={{ fontFamily: "'DM Mono', monospace" }}>{r.device_id}</span>
 <span>{new Date(r.recorded_at).toLocaleTimeString()}</span>
 </div>

 {r.lot_number && (
 <div style={{ marginTop: 6, fontSize: 9, fontFamily: "'DM Mono', monospace", color: T.sub }}>
 Lot: {r.lot_number}
 </div>
 )}
 </div>
 );
 })}
 </div>
 )
 }
 </div>
 </div>
 </div>
 );
}
